/**
 * Role Terminology Manager
 *
 * Admin UI for customizing role display names per organization.
 * Allows orgs to use their preferred terminology (e.g., Promotora, CHW, Navigator).
 */

import React, { useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useRoleTerminology } from '@/hooks/useTeamManagement';
import { CBORoleType, CBO_ROLE_DEFINITIONS, UserOrgRole, USER_ORG_ROLE_TO_CBO_ROLE, USER_ORG_ROLE_INFO } from '@/types/cboRoles';
import {
  RoleTerminologyCreate,
  DEFAULT_ROLE_TERMINOLOGY,
  COMMON_FIELD_WORKER_TERMS,
} from '@/types/roleTerminology';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Pencil, RotateCcw, Save, Languages, Users, ArrowRight, Info } from 'lucide-react';

interface RoleTerminologyManagerProps {
  organizationId: number;
  organizationName: string;
}

export function RoleTerminologyManager({
  organizationId,
  organizationName,
}: RoleTerminologyManagerProps) {
  const { t, language } = useAppTranslation();
  const {
    terminology,
    loading,
    error,
    getRoleName,
    getRoleAbbr,
    updateTerminology,
    deleteTerminology,
  } = useRoleTerminology(organizationId);

  const [editingRole, setEditingRole] = useState<CBORoleType | null>(null);
  const [formData, setFormData] = useState<RoleTerminologyCreate>({
    system_role: 'staff',
    display_name: '',
    display_name_es: '',
    abbreviation: '',
  });
  const [saving, setSaving] = useState(false);

  // Roles that can be customized (6 system roles)
  // Note: Organization-specific terminology (promotora, CHW, navigator, etc.)
  // should be managed via Organization Roles Manager which maps to these system roles.
  const customizableRoles: CBORoleType[] = [
    'staff',
    'supervisor',
    'program_manager',
    'org_admin',
    'viewer',
  ];

  const handleEdit = (role: CBORoleType) => {
    const existing = terminology.find((t) => t.system_role === role);
    const defaults = DEFAULT_ROLE_TERMINOLOGY[role] || { en: role, es: role, abbr: role.substring(0, 3).toUpperCase() };

    setFormData({
      system_role: role,
      display_name: existing?.display_name || defaults.en,
      display_name_es: existing?.display_name_es || defaults.es,
      abbreviation: existing?.abbreviation || defaults.abbr || '',
      description: existing?.description,
      description_es: existing?.description_es,
    });
    setEditingRole(role);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTerminology(formData);
      setEditingRole(null);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (role: CBORoleType) => {
    const existing = terminology.find((t) => t.system_role === role);
    if (existing) {
      await deleteTerminology(existing.id);
    }
  };

  const handleQuickSelect = (term: { en: string; es: string; abbr: string }) => {
    setFormData((prev) => ({
      ...prev,
      display_name: term.en,
      display_name_es: term.es,
      abbreviation: term.abbr,
    }));
  };

  const isCustomized = (role: CBORoleType) => {
    return terminology.some((t) => t.system_role === role);
  };

  // All user_org_roles that can be assigned
  const userOrgRoles: UserOrgRole[] = ['super_admin', 'org_admin', 'ops_admin', 'manager', 'staff', 'viewer', 'member'];

  return (
    <div className="space-y-6">
      {/* Role Mapping Overview */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Info className="w-6 h-6 text-blue-600" />
            <div>
              <CardTitle className="text-blue-900">
                {t('admin.user_role_mapping', 'User Role Mapping')}
              </CardTitle>
              <CardDescription className="text-blue-700">
                {t(
                  'admin.user_role_mapping_desc',
                  'User roles assigned in User Management map to system roles for permissions. The terminology below customizes how these roles are displayed.'
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-blue-900">{t('admin.user_role', 'User Role (Assigned)')}</TableHead>
                <TableHead className="text-center text-blue-900">{t('admin.maps_to', 'Maps To')}</TableHead>
                <TableHead className="text-blue-900">{t('admin.system_role', 'System Role (Permissions)')}</TableHead>
                <TableHead className="text-blue-900">{t('admin.description', 'Description')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {userOrgRoles.map((userRole) => {
                const info = USER_ORG_ROLE_INFO[userRole];
                const cboRole = USER_ORG_ROLE_TO_CBO_ROLE[userRole];

                return (
                  <TableRow key={userRole}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">{info.label}</span>
                        <code className="text-xs bg-blue-100 px-1.5 py-0.5 rounded text-blue-800">
                          {userRole}
                        </code>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <ArrowRight className="w-4 h-4 text-blue-400 mx-auto" />
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-green-100 px-2 py-0.5 rounded text-green-800">
                        {cboRole}
                      </code>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {info.description}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Role Terminology Customization */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Languages className="w-6 h-6 text-primary" />
            <div>
              <CardTitle>
                {t('admin.role_terminology', 'Role Terminology')}
              </CardTitle>
              <CardDescription>
                {t(
                  'admin.role_terminology_desc',
                  'Customize role display names for your organization. For example, change "Promotora" to "Community Health Worker" or "Navigator".'
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.system_role', 'System Role')}</TableHead>
                <TableHead>{t('admin.display_name_en', 'Display Name (English)')}</TableHead>
                <TableHead>{t('admin.display_name_es', 'Display Name (Spanish)')}</TableHead>
                <TableHead>{t('admin.abbreviation', 'Abbr.')}</TableHead>
                <TableHead>{t('admin.status', 'Status')}</TableHead>
                <TableHead className="text-right">{t('admin.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customizableRoles.map((role) => {
                const defaults = DEFAULT_ROLE_TERMINOLOGY[role] || { en: role, es: role, abbr: role.substring(0, 3).toUpperCase() };
                const custom = terminology.find((t) => t.system_role === role);
                const isCustom = !!custom;

                return (
                  <TableRow key={role}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <code className="text-sm bg-muted px-2 py-0.5 rounded">
                          {role}
                        </code>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {custom?.display_name || defaults.en}
                    </TableCell>
                    <TableCell>
                      {custom?.display_name_es || defaults.es}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {custom?.abbreviation || defaults.abbr}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isCustom ? (
                        <Badge variant="default" className="bg-green-100 text-green-800">
                          {t('admin.customized', 'Customized')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          {t('admin.default', 'Default')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(role)}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          {t('admin.edit', 'Edit')}
                        </Button>
                        {isCustom && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReset(role)}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            {t('admin.reset', 'Reset')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {error && (
            <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingRole} onOpenChange={() => setEditingRole(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('admin.edit_role_terminology', 'Edit Role Terminology')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'admin.edit_role_terminology_desc',
                'Customize how this role is displayed throughout the platform for your organization.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>{t('admin.system_role', 'System Role')}</Label>
              <Input value={editingRole || ''} disabled className="bg-muted" />
            </div>

            {/* Quick Select for common field worker terms */}
            {editingRole === 'promotora' && (
              <div>
                <Label>{t('admin.quick_select', 'Quick Select')}</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  {t('admin.quick_select_desc', 'Choose from common terminology:')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {COMMON_FIELD_WORKER_TERMS.slice(0, 6).map((term, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickSelect(term)}
                      className={
                        formData.display_name === term.en
                          ? 'border-primary bg-primary/10'
                          : ''
                      }
                    >
                      {term.en}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="display_name">
                {t('admin.display_name_en', 'Display Name (English)')}
              </Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, display_name: e.target.value }))
                }
                placeholder="e.g., Community Health Worker"
              />
            </div>

            <div>
              <Label htmlFor="display_name_es">
                {t('admin.display_name_es', 'Display Name (Spanish)')}
              </Label>
              <Input
                id="display_name_es"
                value={formData.display_name_es}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, display_name_es: e.target.value }))
                }
                placeholder="e.g., Trabajador de Salud Comunitaria"
              />
            </div>

            <div>
              <Label htmlFor="abbreviation">
                {t('admin.abbreviation', 'Abbreviation')}
              </Label>
              <Input
                id="abbreviation"
                value={formData.abbreviation || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, abbreviation: e.target.value }))
                }
                placeholder="e.g., CHW"
                maxLength={5}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('admin.abbreviation_hint', 'Short code shown in compact views (max 5 chars)')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRole(null)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('admin.preview', 'Preview')}
          </CardTitle>
          <CardDescription>
            {t('admin.preview_desc', 'How roles will appear in the application')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {customizableRoles.map((role) => (
              <div
                key={role}
                className="p-3 rounded-lg border bg-card text-center"
              >
                <Badge variant="outline" className="mb-2">
                  {getRoleAbbr(role)}
                </Badge>
                <p className="font-medium text-sm">
                  {getRoleName(role, language as 'en' | 'es')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {language === 'es'
                    ? getRoleName(role, 'en')
                    : getRoleName(role, 'es')}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default RoleTerminologyManager;
